/**
 * @file device-flag.ts
 * @description Флаг-настройка рабочего места (этого браузера) в `localStorage`
 *   с шиной подписчиков — фабрикой (phase19.22 шаг 5, «Линии реза»).
 *
 * Механизм тот же, что у «звука скана» (`scan-feedback.ts`): источник правды —
 * переменная в памяти, `localStorage` только переживает перезагрузку (в
 * приватном режиме он молча не сохраняет — переключатель не должен «отпрыгивать»).
 * Шина нужна, чтобы все переключатели одной настройки показывали одно и то же
 * без перезагрузки. Новая настройка устройства = один вызов фабрики.
 *
 * ```ts
 * export const cutLinesFlag = createDeviceFlag("chaiberi.admin.labels.cutLines", false);
 * cutLinesFlag.set(true);
 * ```
 */

/** Флаг устройства: чтение, запись и подписка (для `useSyncExternalStore`). */
export interface DeviceFlag {
  get: () => boolean;
  set: (value: boolean) => void;
  subscribe: (listener: () => void) => () => void;
  /** Значение по умолчанию — снимок без `window` (SSR/тесты). */
  defaultValue: boolean;
}

export function createDeviceFlag(storageKey: string, defaultValue: boolean): DeviceFlag {
  let cache: boolean | null = null;
  const listeners = new Set<() => void>();

  const get = (): boolean => {
    if (cache === null) {
      try {
        const raw = localStorage.getItem(storageKey);
        cache = raw === null ? defaultValue : raw === "on";
      } catch {
        // Хранилище недоступно (приватный режим) — берём значение по умолчанию.
        cache = defaultValue;
      }
    }
    return cache;
  };

  const set = (value: boolean): void => {
    cache = value;
    try {
      localStorage.setItem(storageKey, value ? "on" : "off");
    } catch {
      // Не критично: настройка просто не переживёт перезагрузку.
    }
    for (const listener of listeners) listener();
  };

  const subscribe = (listener: () => void): (() => void) => {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  };

  return { get, set, subscribe, defaultValue };
}
